<template>
  <div>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <VTableRemote
      v-else
      ref="tableRemoteRef"
      :items="samplesRegister"
      :isSearchable="false"
      :headers="tableHeaders"
      :disablePagination="true"
      :title="$t('laboratory.samplesRegister')"
      :isFilterEnable="true"
      :filterItems="getFilterItems"
      :multiple="true"
      :isInfinitScroll="true"
      hideDefaultFooter
      :pagination="pagination"
      :sortableColumns="sortableColumns"
      :centerColumns="['labWorkOrder']"
      :notSortableColumns="['labWorkOrder']"
      @filter-type-changed="filterChanged"
      @sendQueryParams="fetchQuery"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :filters="filters"
          :searchPlaceholder="$t('laboratory.placeholder.searchBy')"
          :filterName="filterName"
          :saveFilter="true"
          @onApplyFilters="handleApplyFilters"
          @onSearch="handleSearch"
        />
      </template>
      <template #item.type="{item}">
        {{ item.type }}
      </template>
      <template #item.siteName="{ item }">
        {{ item.site.name }}
      </template>
      <template #item.siteNumber="{item}">
        {{ item.site.number }}
      </template>
      <template #item.collectedDate="{item}">
        <span>{{ $options.filters.formatDate(item.collectedDate, 'L LT') }}</span>
      </template>
      <template #item.receivedDate="{item}">
        <span>{{ $options.filters.formatDate(item.receivedDate, 'L LT') }}</span>
      </template>
      <template #item.requestedBy="{item}">
        <p v-if="item.requestedBy">
          {{ item.requestedBy.firstName }} {{ item.requestedBy.lastName }}
        </p>
      </template>
      <template #item.status="{item}">
        <p v-if="item.requestedBy">
          {{ getStatus(item) }}
        </p>
      </template>
      <template #item.labWorkOrder="{item}">
        <div class="linkWO">
          <v-btn
            tile
            icon
            size="24"
            color="#26384b"
            :disabled="!item.labWorkOrder.id"
            @click="onClickRedirect(item.labWorkOrder.id, item.client.id, item.site.id)"
          >
            <v-icon class="link">
              mdi-link-variant
            </v-icon>
          </v-btn>
        </div>
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </VTableRemote>
  </div>
</template>
<script>

import { debounce, isEmpty } from 'lodash';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import sampleStatus from '@/constants/sampleStatus';
import sampleTypes from '@/constants/sampleTypes';
import moment from 'moment';
import filterMixin from '@/mixins/filterMixin';

export default {
    name: 'SamplesRegister',
    components: {
        VTableRemote,
        SearchWithFilters,
    },
    mixins: [
        filterMixin,
    ],
    data() {
        return {
            isInitializing: false,
            samplesRegister: [],
            loading: false,
            pagination: {},
            filterName: 'samplesRegister',
            queryParams: { perpage: 25, status: ['PENDING', 'ACCEPTED', 'INTREATMENT'] },
            debounceFetchRegister: debounce(this.getSamplesRegister, 500),
            filterItems: ['PENDING', 'ACCEPTED', 'REFUSED', 'ACCEPTEDNOTCONFORMED', 'INTREATMENT', 'COMPLETED'],
            filters: [
                {
                    key: 'type',
                    label: 'laboratory.labels.type',
                    type: 'select',
                    value: [],
                    multiple: true,
                    items: Object.values(sampleTypes).map(sType => ({
                        text: `sampleTypes.${sType.charAt(0).toLowerCase()}${sType.substring(1)}`,
                        value: sType,
                    })),
                },
                {
                    key: 'status',
                    label: 'laboratory.labels.status',
                    type: 'select',
                    value: ['PENDING', 'ACCEPTED', 'INTREATMENT'],
                    multiple: true,
                    items: [...Object.keys(sampleStatus), 'INTREATMENT', 'COMPLETED'].map(sStatus => ({
                        text: `laboratory.labels.${sStatus.toLowerCase()}`,
                        value: sStatus,
                    })),
                },
                {
                    key: 'collectedDate',
                    label: 'laboratory.labels.collectedDate',
                    type: 'date',
                    value: [moment().subtract(30, 'days').toISOString(), moment().toISOString()],
                    dateKeys: ['collectedStartDate', 'collectedEndDate'],
                    isRange: true,
                    dataCY: 'samplesRegisterCollectedDate',
                },
                {
                    key: 'receivedDate',
                    label: 'laboratory.labels.receivedDate',
                    type: 'dateWithCheckbox',
                    value: [moment().subtract(30, 'days').toISOString(), moment().toISOString()],
                    dateKeys: ['receivedStartDate', 'receivedEndDate'],
                    dataCY: 'samplesRegisterReceivedDate',
                    samplesReceived: true,
                },
            ],
        };
    },
    computed: {
        headers() {
            return [
                'identification',
                'type',
                'regulatoryIdentification',
                'siteName',
                'siteNumber',
                'labId',
                'collectedDate',
                'receivedDate',
                'requestedBy',
                'labWorkOrder',
                'status',
            ];
        },
        tableHeaders() {
            return this.headers.map(header => ({
                text: this.$t(`laboratory.labels.${header}`),
                value: header,
            }));
        },
        getFilterItems() {
            return this.filterItems.map((item, key) => ({
                key,
                name: this.$t(`laboratory.labels.${item.toLowerCase()}`),
            }));
        },
        sortableColumns() {
            return this.headers.filter(el => el !== 'labWorkOrder').map(column => this.$options.filters.lowercase(column));
        },
    },

    async created() {
        this.isInitializing = true;
        this.getFilterValues();
        await this.getSamplesRegister();
        this.isInitializing = false;
    },
    methods: {
        async getSamplesRegister() {
            if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);

                this.loading = true;
                const { data } = await this.$rq.getSampleRequests(cleanQueryParams);
                this.samplesRegister = [...this.samplesRegister, ...data.samples];
                this.pagination = data.pagination;
                this.loading = false;
            }
            this.loading = false;
        },
        async fetchQuery(queryParams) {
            if (!this.loading) {
                this.queryParams = {
                    ...this.queryParams,
                    ...queryParams,
                    page: 1,
                    order: this.preparOrderQueryParams(queryParams.order),
                };
                this.pagination = {};
                this.samplesRegister = [];
                this.getSamplesRegister();
            }
        },

        preparOrderQueryParams(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;
            switch (name) {
                case 'identification':
                    newValue = 'identification';
                    break;
                case 'type':
                    newValue = 'type';
                    break;
                case 'regulatoryidentification':
                    newValue = 'regulatoryidentification';
                    break;
                case 'sitename':
                    newValue = 'sitename';
                    break;
                case 'sitenumber':
                    newValue = 'sitenumber';
                    break;
                case 'labid':
                    newValue = 'labid';
                    break;
                case 'collecteddate':
                    newValue = 'collecteddate';
                    break;
                case 'receiveddate':
                    newValue = 'receiveddate';
                    break;
                case 'requestedby':
                    newValue = 'requestedby';
                    break;
                case 'status':
                    newValue = 'status';
                    break;
                default:
                    return null;
            }
            return `${newValue}_${order}`;
        },

        onWoClick() {
            this.$router.push('labWorkOrders');
        },
        onClickRedirect(labId, clientid, siteid) {
            this.$router.push({
                name: 'labWorkOrderView',
                params: {
                    labWorkOrderId: labId,
                    clientId: clientid,
                    siteId: siteid,
                },
            });
        },
        getStatus(item) {
            return this.$t(`laboratory.labels.${item.status.toLowerCase()}`);
        },
        filterChanged(selectedStatus) {
            const status = selectedStatus.map(st => this.filterItems[st]);
            this.pagination = { ...this.pagination, status, page: 1 };
            this.pagination = {};
            this.samplesRegister = [];
            this.debounceFetchRegister();
        },
        handleApplyFilters(filterParams) {
            this.queryParams = {
                ...this.queryParams,
                ...filterParams,
            };

            // reset filter if needed
            if (filterParams?.reset) {
                this.getFilterValues(true);
            }
            this.pagination = {};
            this.samplesRegister = [];
            this.getSamplesRegister();
        },
        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
                page: 1,
            };
            this.pagination = {};
            this.samplesRegister = [];
            this.debounceFetchRegister();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getSamplesRegister();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
  .linkWO {
    text-align: center;
    }

</style>
